"use client";

import { useRouter } from "next/navigation";
import React from "react";
import { FaCheck } from "react-icons/fa";
import Image from "next/image";

// --- Check List Item (dark bg) ---
const CheckListItemDark = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <span className="w-5 h-5 flex-shrink-0 rounded-full bg-purple-500 text-white flex items-center justify-center mt-0.5">
      <FaCheck size={9} />
    </span>
    <span className="text-purple-100 text-sm sm:text-base leading-relaxed">
      {text}
    </span>
  </li>
);

// --- Check List Item (light bg) ---
const CheckListItemLight = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <span className="w-5 h-5 flex-shrink-0 rounded-full bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300 flex items-center justify-center mt-0.5">
      <FaCheck size={9} />
    </span>
    <span className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
      {text}
    </span>
  </li>
);

// --- Stat Card ---
const StatCard = ({ value, label }: { value: string; label: string }) => (
  <div className="text-center">
    <p className="text-3xl font-extrabold text-white">{value}</p>
    <p className="text-purple-300 text-xs mt-1">{label}</p>
  </div>
);

export default function ExperiencePage() {
  const router = useRouter();

  const whyBookFeatures = [
    "Handpicked, verified local hosts and guides",
    "Instant confirmation on most experiences",
    "Free cancellation up to 24 hours before",
    "Small-group experiences for a personal touch",
  ];

  const hostBenefits = [
    "Share your passion and earn extra income",
    "Set your own schedule and group sizes",
    "Dedicated support from the Lily Lanka team",
    "Reach thousands of local and international travelers",
  ];

  const categories = [
    { icon: "🏄", label: "Adventures & Outdoors" },
    { icon: "🍛", label: "Culinary & Local Life" },
    { icon: "🏯", label: "Culture & Heritage" },
    { icon: "🧘", label: "Wellness & Relaxation" },
    { icon: "🎉", label: "Seasonal Festivals" },
    { icon: "🦚", label: "Wildlife & Nature" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-950">
      <main className="flex-1 w-full">
        {/* ===== HERO — Split layout, diagonal accent ===== */}
        <section className="relative bg-purple-900 overflow-hidden">
          {/* Decorative diagonal */}
          <div className="absolute bottom-0 right-0 w-1/2 h-full bg-purple-800/40 skew-x-6 origin-bottom-right hidden lg:block" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-24 md:pt-30 pb-12">
            {" "}
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              {/* LEFT */}
              <div className="flex-1 text-center lg:text-left">
                <span className="inline-block bg-purple-700 text-purple-200 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5">
                  🇱🇰 Authentic Sri Lankan Experiences
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4">
                  Discover{" "}
                  <span className="text-purple-400">Unforgettable</span>
                  <br className="hidden sm:block" /> Experiences
                </h1>
                <p className="text-purple-200 text-sm sm:text-base leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
                  Go beyond the stay — dive into authentic local adventures,
                  cultural encounters, culinary journeys, and wildlife
                  experiences curated by passionate locals who know Sri Lanka
                  best.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <button
                    onClick={() =>
                      router.push("/experience/sign-in-experience")
                    }
                    className="bg-purple-500 hover:bg-purple-400 text-white font-semibold py-3 px-8 rounded-lg transition-colors text-sm cursor-pointer"
                  >
                    Sign in
                  </button>
                  <button
                    onClick={() =>
                      router.push("/experience/sign-up-experience")
                    }
                    className="bg-transparent hover:bg-purple-700 text-purple-300 hover:text-white font-semibold py-3 px-8 rounded-lg border border-purple-500 transition-colors text-sm cursor-pointer"
                  >
                    Become a Host
                  </button>
                </div>
              </div>

              {/* RIGHT — Category grid */}
              {/* <div className="flex-1 w-full max-w-sm lg:max-w-none">
                <div className="relative rounded-2xl overflow-hidden">
                  <Image
                    src="/experience.png"
                    alt="Sri Lanka experiences"
                    width={600}
                    height={400}
                    className="w-full h-full object-cover rounded-2xl"
                    priority
                  />
                </div>
              </div> */}
            </div>
          </div>
        </section>

        {/* ===== WHY BOOK + HOST — Two column cards ===== */}
        <section className="bg-white dark:bg-gray-900 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Why Book */}
            <div className="bg-purple-50 dark:bg-purple-950 rounded-2xl p-8 border border-purple-100 dark:border-purple-800">
              <p className="text-purple-500 text-xs font-bold tracking-widest uppercase mb-3">
                Why Book With Us
              </p>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                The best way to explore Sri Lanka
              </h2>
              <div className="w-8 h-0.5 bg-purple-400 rounded mb-5" />
              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-5">
                We make it easy to explore Sri Lanka authentically — from
                sunrise hikes to cooking classes and wildlife safaris.
              </p>
              <ul className="space-y-3 mb-6">
                {whyBookFeatures.map((item, i) => (
                  <CheckListItemLight key={i} text={item} />
                ))}
              </ul>
              <button
                onClick={() => router.push("/activities")}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors text-sm cursor-pointer"
              >
                Browse All Experiences
              </button>
            </div>

            {/* Become a Host */}
            <div className="bg-purple-900 rounded-2xl p-8 border border-purple-700">
              <p className="text-purple-400 text-xs font-bold tracking-widest uppercase mb-3">
                Become a Host
              </p>
              <h2 className="text-xl font-bold text-white mb-2">
                Share your passion, earn income
              </h2>
              <div className="w-8 h-0.5 bg-purple-500 rounded mb-5" />
              <p className="text-purple-200 text-sm leading-relaxed mb-5">
                Are you a local guide, chef, wellness instructor, or adventurer?
                Turn your passion into a business and share it with travelers
                from around the world.
              </p>
              <ul className="space-y-3 mb-6">
                {hostBenefits.map((item, i) => (
                  <CheckListItemDark key={i} text={item} />
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => router.push("/experience/sign-in-experience")}
                  className="bg-purple-500 hover:bg-purple-400 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors text-sm cursor-pointer"
                >
                  Sign In
                </button>
                <button
                  onClick={() => router.push("/experience/sign-up-experience")}
                  className="bg-transparent hover:bg-purple-700 text-purple-300 hover:text-white font-semibold py-2.5 px-6 rounded-lg border border-purple-500 transition-colors text-sm cursor-pointer"
                >
                  Become a Host
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ===== IDENTITY BANNER ===== */}
        <section className="bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 text-center border-t border-gray-100 dark:border-gray-800">
          <div className="max-w-2xl mx-auto">
            <p className="text-purple-500 text-xs font-bold tracking-widest uppercase mb-3">
              🇱🇰 Our Identity
            </p>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Built by Sri Lankans, for Sri Lankans, for Sri Lanka
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
              Sri Lanka's first fully operational SLTDA-registered web &amp; app
              platform — connecting travelers with authentic local experiences.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
